import { useCallback, useEffect, useRef, useState } from "react";
import { NotificationManager } from "../notifications/NotificationManager";
import SockJS from "sockjs-client";
import Stomp from "stompjs";
import { useAuth } from "../context/UserContextProvider";
import endpoints from "../config/endpoints.json";
import connection_production from "../config/connection-production.json";
import connection_local from "../config/connection-local.json";
import connection_development from "../config/connection-development.json";
import i18n from "../language/i18n";
import useTabActive from "../hooks/useTabActive";
import { isTokenExpired } from "../utilization/JwtUtilization";

const connection =
  process.env.REACT_APP_NODE_ENV === "local"
    ? connection_local
    : process.env.REACT_APP_NODE_ENV === "development"
      ? connection_development
      : process.env.REACT_APP_NODE_ENV === "production"
        ? connection_production
        : connection_local;

export const useWebSocket = () => {
  const {
    userInformation,
    userProfile,
    userProfileSeason,
    handleStateLogout,
    webSocketStompClient,
    setWebSocketStompClient,
    setWebSocketConnectionStatus,
    setSessionInactiveNoticeOpen,
  } = useAuth();

  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [lastConnectionTime, setLastConnectionTime] = useState(null);

  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const tabReconnectTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const inactivityTimeoutRef = useRef(null);
  const isConnectingRef = useRef(false);

  const isTabActive = useTabActive();

  const maxReconnectAttempts = 3;
  const baseReconnectDelay = 3000;
  const maxReconnectDelay = 30000;
  const maxInactiveTime = 30 * 60 * 1000;

  const updateLastActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  const getReconnectDelay = useCallback((attempt) => {
    const delay = Math.min(
      baseReconnectDelay * Math.pow(2, attempt),
      maxReconnectDelay,
    );
    const jitter = Math.random() * 1000;
    return delay + jitter;
  }, []);

  const clearTimers = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
      inactivityTimeoutRef.current = null;
    }
    if (tabReconnectTimeoutRef.current) {
      clearTimeout(tabReconnectTimeoutRef.current);
      tabReconnectTimeoutRef.current = null;
    }
  }, []);

  const unsubscribeAll = useCallback(() => {
    if (webSocketStompClient?.connected) {
      try {
        Object.keys(webSocketStompClient.subscriptions || {}).forEach((id) => {
          try {
            webSocketStompClient.unsubscribe(id);
          } catch (error) {
            console.warn(`Error unsubscribing from subscription ${id}:`, error);
          }
        });
      } catch (error) {
        console.warn("Error during bulk unsubscribe:", error);
      }
    }
  }, [webSocketStompClient]);

  const handleLogout = useCallback(() => {
    handleStateLogout();
    NotificationManager.success(i18n.t("logout_successful_text"));
  }, [handleStateLogout]);

  const disconnect = useCallback(() => {
    clearTimers();
    unsubscribeAll();
    isConnectingRef.current = false;

    if (webSocketStompClient?.connected) {
      try {
        webSocketStompClient.disconnect();
      } catch (error) {
        console.warn("Error during disconnect:", error);
      }
    }

    setWebSocketStompClient(null);
    setWebSocketConnectionStatus("disconnected");
    reconnectAttemptsRef.current = 0;
    setReconnectAttempts(0);
    setLastConnectionTime(null);
  }, [
    webSocketStompClient,
    clearTimers,
    unsubscribeAll,
    setWebSocketStompClient,
    setWebSocketConnectionStatus,
  ]);

  const disconnectDueToInactivity = useCallback(() => {
    // A MODAL, not a toast. This fires after 30 minutes of inactivity — i.e. precisely when nobody is
    // looking at the screen — so the toast this replaced was gone long before the learner came back,
    // and they resumed on an app that was silently receiving nothing (duel invites, friend presence).
    // SessionInactiveModal persists until the connection is actually restored.
    //
    // It also retires a latent bug: the local NotificationManager.info takes ONE argument, so the
    // title and the 5000ms duration were being dropped on the floor at runtime.
    setSessionInactiveNoticeOpen(true);
    disconnect();
  }, [disconnect, setSessionInactiveNoticeOpen]);

  const setupInactivityTimer = useCallback(() => {
    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    // Arm for the REMAINING inactivity budget, not a fresh full window: real
    // activity call sites run updateLastActivity() first (full window), while
    // passive re-arms (tab toggles, reconnects) must not push the reap past
    // maxInactiveTime since the last actual activity.
    const elapsedSinceActivity = Date.now() - lastActivityRef.current;
    inactivityTimeoutRef.current = setTimeout(
      () => {
        disconnectDueToInactivity();
      },
      Math.max(0, maxInactiveTime - elapsedSinceActivity),
    );
  }, [disconnectDueToInactivity, maxInactiveTime]);

  const setupSubscriptions = useCallback(
    (stompClient) => {
      if (!stompClient || !userProfile) return;

      try {
        stompClient.subscribe(
          `/${userProfile.id}/management/update-user-profile`,
          (message) => {
            updateLastActivity();
            const { requiredAction } = JSON.parse(message.body);

            if (requiredAction === "PROFILE_SELECTION") {
              window.location.replace(connection.admin.url);
            } else if (requiredAction === "LOGOUT") {
              handleLogout();
            }
          },
        );
      } catch (error) {
        console.error("Error setting up subscriptions:", error);
      }
    },
    [userProfile, handleLogout, updateLastActivity, isTabActive],
  );

  const debouncedReconnect = useCallback(
    (accessToken, delay = 1000) => {
      if (tabReconnectTimeoutRef.current) {
        clearTimeout(tabReconnectTimeoutRef.current);
        tabReconnectTimeoutRef.current = null;
      }

      tabReconnectTimeoutRef.current = setTimeout(() => {
        if (
          isTabActive &&
          !isConnectingRef.current &&
          !webSocketStompClient?.connected &&
          userInformation &&
          userProfile
        ) {
          connectWebSocket(accessToken, true);
        }
        tabReconnectTimeoutRef.current = null;
      }, delay);
    },
    [isTabActive, webSocketStompClient, userInformation, userProfile],
  );

  const connectWebSocket = useCallback(
    (accessToken, bypassTabCheck = false) => {
      if (!userInformation || !userProfile) {
        return;
      }
      if (!bypassTabCheck && !isTabActive) {
        return;
      }
      if (isConnectingRef.current) {
        return;
      }
      if (webSocketStompClient?.connected) {
        return;
      }
      if (tabReconnectTimeoutRef.current) {
        clearTimeout(tabReconnectTimeoutRef.current);
        tabReconnectTimeoutRef.current = null;
      }

      isConnectingRef.current = true;
      setWebSocketConnectionStatus("connecting");

      clearTimers();

      try {
        const socket = new SockJS(
          `${connection.base.websocket.url}${endpoints.websocket.context}${endpoints.websocket.web.connect.context}`,
        );
        const stompClient = Stomp.over(socket);

        // Silence STOMP's own frame logging. Stomp.js writes every frame to the console when
        // `debug` is left unset, and the frames carry the X-Authorization bearer token — which is
        // why this hook existed in the first place (it filtered those frames out before logging the
        // rest). With the frame logging gone the filter has nothing to decide, but the sink must
        // stay: dropping it hands the library back its default and puts the token in the console.
        stompClient.debug = () => {};

        const { role } = userProfile;

        let details = null;
        if (role === "LEARNER") {
          const { institutionDTO } = userProfileSeason;
          const { campusDTO } = institutionDTO;
          const { brandDTO } = campusDTO;
          details = {
            brandId: brandDTO.id,
            brandName: brandDTO.name,
            campusId: campusDTO.id,
            campusName: campusDTO.name,
            institutionId: institutionDTO.id,
            institutionName: institutionDTO.name,
            role,
          };
        }

        const connectHeaders = {
          "X-Authorization": "Bearer " + accessToken,
          "X-Details": JSON.stringify(details),
        };

        stompClient.connect(
          connectHeaders,
          () => {
            isConnectingRef.current = false;
            setWebSocketStompClient(stompClient);
            setWebSocketConnectionStatus("connected");
            // The idle notice is NOT cleared here, deliberately. Coming back is not the same as
            // being told: this handler runs for every reconnect route, and most of them are ones the
            // learner never asked for — the document activity listener (a single mousedown/keypress/
            // scroll/click anywhere), the tab-visibility reconnect, this callback's own backoff. So
            // clearing it here took the notice off the screen on the first stray click, before it had
            // been read, which defeats the only reason it exists: telling someone who was away that
            // invites and updates were missed while the socket was down. `SessionInactiveModal` owns
            // the notice's lifetime now and resolves it on the "Yeniden bağlan" press alone (that
            // press closes it immediately when the status is already "connected", so a socket that
            // healed on its own costs nobody an extra wait). Raising it stays here — see
            // `disconnectDueToInactivity`.
            reconnectAttemptsRef.current = 0;
            setReconnectAttempts(0);
            setLastConnectionTime(new Date());
            updateLastActivity();

            // Always arm the reaper — hidden tabs keep their socket now, so
            // the inactivity timeout is what bounds a connection's lifetime.
            setupInactivityTimer();
            setupSubscriptions(stompClient);
          },
          (error) => {
            console.error("WebSocket connection failed:", error);
            isConnectingRef.current = false;
            setWebSocketConnectionStatus("disconnected");
            setWebSocketStompClient(null);

            if (
              isTabActive &&
              reconnectAttemptsRef.current < maxReconnectAttempts
            ) {
              const delay = getReconnectDelay(reconnectAttemptsRef.current);

              reconnectTimeoutRef.current = setTimeout(() => {
                setReconnectAttempts((prev) => {
                  reconnectAttemptsRef.current = prev + 1;
                  return reconnectAttemptsRef.current;
                });
                // The connection can outlive a token refresh, so retry with
                // the current token rather than the one it was opened with.
                const storedToken = localStorage.getItem("accessToken");
                connectWebSocket(
                  storedToken && !isTokenExpired(storedToken)
                    ? storedToken
                    : accessToken,
                );
              }, delay);
            } else {
              console.error(
                "Max reconnection attempts reached or tab not active",
              );
              if (isTabActive) {
                // Same legacy 3-arg form as disconnectDueToInactivity above.
                (NotificationManager.error as (...a: unknown[]) => void)(
                  i18n.t("connection_error_text"),
                  i18n.t("connection_error"),
                  0,
                );
              }
            }
          },
        );
      } catch (error) {
        console.error("Error creating WebSocket connection:", error);
        isConnectingRef.current = false;
        setWebSocketConnectionStatus("disconnected");
        setWebSocketStompClient(null);
      }
    },
    [
      userInformation,
      userProfile,
      userProfileSeason,
      webSocketStompClient,
      isTabActive,
      getReconnectDelay,
      setupSubscriptions,
      setupInactivityTimer,
      clearTimers,
      updateLastActivity,
      setWebSocketStompClient,
      setWebSocketConnectionStatus,
    ],
  );

  useEffect(() => {
    const handleActivity = () => {
      updateLastActivity();

      if (webSocketStompClient?.connected && isTabActive) {
        setupInactivityTimer();
      } else if (
        !webSocketStompClient?.connected &&
        !isConnectingRef.current &&
        isTabActive &&
        userInformation &&
        userProfile
      ) {
        const accessToken = localStorage.getItem("accessToken");
        if (accessToken && !isTokenExpired(accessToken)) {
          connectWebSocket(accessToken);
        }
      }
    };

    const activityEvents = ["mousedown", "keypress", "scroll", "click"];

    activityEvents.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
    };
  }, [
    webSocketStompClient?.connected,
    isTabActive,
    userInformation,
    userProfile,
    updateLastActivity,
    setupInactivityTimer,
    connectWebSocket,
  ]);

  useEffect(() => {
    if (isTabActive) {
      updateLastActivity();

      if (
        !webSocketStompClient?.connected &&
        !isConnectingRef.current &&
        userInformation &&
        userProfile
      ) {
        const accessToken = localStorage.getItem("accessToken");
        if (accessToken && !isTokenExpired(accessToken)) {
          debouncedReconnect(accessToken, 1000);
        }
      }

      if (webSocketStompClient?.connected) {
        setupInactivityTimer();
      }
    } else {
      // Tab hidden: keep a healthy socket alive — duels, duel invites and
      // presence must survive tab switches. The inactivity timer still reaps
      // the connection if the tab stays hidden past maxInactiveTime.
      if (webSocketStompClient?.connected) {
        setupInactivityTimer();
      }
    }
  }, [isTabActive, userInformation, userProfile]);

  useEffect(() => {
    if (webSocketStompClient?.connected && isTabActive) {
      setupInactivityTimer();
    }
  }, [webSocketStompClient?.connected, isTabActive, setupInactivityTimer]);

  useEffect(() => {
    const handleTokenRefreshed = () => {
      // A live STOMP session was authenticated at CONNECT time and needs no
      // re-auth — bouncing it here flapped "disconnected" mid-duel. The fresh
      // token is read from localStorage on the next (re)connect instead.
      if (webSocketStompClient?.connected || isConnectingRef.current) {
        return;
      }
      const freshAccessToken = localStorage.getItem("accessToken");
      if (freshAccessToken && userInformation && userProfile && isTabActive) {
        debouncedReconnect(freshAccessToken, 500);
      }
    };

    window.addEventListener("tokenRefreshed", handleTokenRefreshed);
    return () => {
      window.removeEventListener("tokenRefreshed", handleTokenRefreshed);
    };
  }, [
    webSocketStompClient?.connected,
    disconnect,
    debouncedReconnect,
    userInformation,
    userProfile,
    isTabActive,
  ]);

  const sendMessage = useCallback(
    (destination, headers = {}, body = "") => {
      if (!webSocketStompClient?.connected) {
        console.warn("Cannot send message: WebSocket not connected");
        return false;
      }

      try {
        webSocketStompClient.send(destination, headers, body);
        return true;
      } catch (error) {
        console.error("Error sending message:", error);
        return false;
      }
    },
    [webSocketStompClient],
  );

  return {
    reconnectAttempts,
    lastConnectionTime,
    connect: connectWebSocket,
    disconnect,
    sendMessage,
  };
};
